<template>
  <div>
    <div ref="trigger">
      <CombinedSearch
        ref="searchInput"
        label="Customer Lookup"
        placeholder="Search…(min. 4 characters or a customer ID)"
        show-clear-button
        :validation="Boolean(validation) ? 'error' : ''"
        :helperText="validation"
        @update:searchQuery="handleTyping"
        @search="handleFormSubmit"
      />
    </div>
    <SafeTeleport to="body">
      <div
        v-if="isOpen"
        ref="results"
        data-cy="customer-lookup-panel"
        class="flex flex-col overflow-hidden bg-white rounded-lg shadow dark:bg-gray-700 z-[9999]"
        :class="floatingStyle ? '' : 'invisible'"
        :style="floatingStyle"
      >
        <ul class="overflow-y-auto flex-1 min-h-0">
          <li v-if="searching" class="p-2">
            <Spinner size="small" />
          </li>
          <li
            v-for="(result, i) in lookupResults"
            :key="result.customer?.id + '-index-' + i"
            class="p-2 hover:bg-blue-50 hover:text-blue-700"
            @mousedown.stop.prevent="selectCustomer(result)"
          >
            <div class="flex flex-col">
              <span class="font-medium flex items-center gap-1">
                {{
                  result.customer.flattened_name ||
                  `${result.customer.first_name} ${result.customer.last_name}`
                }}
                <img
                  v-if="result.customer.membership?.icon_url"
                  :src="result.customer.membership.icon_url"
                  :title="result.customer.membership.membership_program_name"
                  :alt="result.customer.membership.membership_program_name"
                  width="15"
                  height="15"
                  class="inline-block"
                />
              </span>
              <span class="text-xs text-gray-500 pointer-events-none">{{
                fullAddress(result.address)
              }}</span>
            </div>
          </li>
          <li v-if="!lookupResults.length && submitted" class="p-2">
            <div class="flex flex-col">
              <span class="text-gray-500 text-xs">No Results</span>
            </div>
          </li>
        </ul>
        <div
          v-if="createCustomerEnabled"
          class="shrink-0 p-2 border-t border-gray-100 hover:bg-blue-50 cursor-pointer"
          data-cy="create-new-customer"
          @mousedown.stop.prevent="goToNewCustomer"
        >
          <span class="font-medium text-blue-700">＋ Create New Customer</span>
        </div>
      </div>
    </SafeTeleport>
  </div>
</template>

<script setup lang="ts">
import debounce from "lodash.debounce";
import { onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { SafeTeleport } from "vue-safe-teleport";
import { useStore } from "vuex";
import { isSearchableTerm } from "@/lib/customerSearchTerm";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import CombinedSearch from "@/components/Seranova/SearchInput/CombinedSearch.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { useAnchoredDropdown } from "@/composables/useAnchoredDropdown";
import { fullAddress } from "@/mixins";
import { userCreatePath } from "@/mixins/path_helpers";
import { Address } from "@/typings/address";
const emit = defineEmits(["update:modelValue"]);
const router = useRouter();
const createCustomerEnabled = useFeatureFlag(
  "create_customer_from_dispatch_ff_enabled"
);
const lookupResults = ref<{ customer: LookupResult; address: Address }[]>([]);
const trigger = ref<HTMLElement | null>(null);
const results = ref<HTMLElement | null>(null);
const store = useStore();
const isOpen = ref(false);
const searching = ref(false);
const searchInput = ref(null);
const searchTerm = ref("");
const submitted = ref(false);
defineProps<{ validation: string }>();

export interface LookupResult {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  full_name: string;
  match_score: number;
  membership?: {
    icon_url?: string;
    membership_program_name?: string;
  } | null;
  addresses: {
    id: number;
    matches: { [k: string]: [{ start: number; end: number }] };
    street: string;
  };
  all_addresses?: Address[];
  flattened_name: string;
  has_subs: boolean;
  parent?: null;
  matches: { [k: string]: [{ start: number; end: number }] }[];
}

// Matches the `max-h-96` the list used to carry. That cap covered the create-new
// row too — it was the last item inside the list — so the same number belongs on
// the whole panel now the row sits outside it, and desktop keeps its height.
const PANEL_PREFERRED_HEIGHT = 384;
// What Flowbite placed this menu with before it moved onto the shared geometry.
const PANEL_OFFSET = 10;

const { floatingStyle } = useAnchoredDropdown({
  anchor: trigger,
  isOpen,
  offset: PANEL_OFFSET,
  preferredHeight: PANEL_PREFERRED_HEIGHT,
  onAnchorHidden: () => {
    isOpen.value = false;
  }
});

// Flowbite dismissed the menu on an outside click; the shared positioning code
// does not, so the listener is kept by hand. Capture phase on `body` for the
// reason Flowbite chose it: a handler that stops propagation further down would
// otherwise leave the menu open forever.
const isInsideDropdown = (target: Node | null) =>
  Boolean(
    target &&
    (trigger.value?.contains(target) || results.value?.contains(target))
  );

const handleClickOutside = (event: MouseEvent) => {
  if (!isInsideDropdown(event.target as Node)) isOpen.value = false;
};

const stopWatchingClicks = () =>
  document.body.removeEventListener("click", handleClickOutside, true);

watch(isOpen, (open) => {
  if (open) {
    document.body.addEventListener("click", handleClickOutside, true);
  } else {
    stopWatchingClicks();
  }
});

onBeforeUnmount(stopWatchingClicks);

const executeSearch = debounce(async (query: string) => {
  // If the endpoint would refuse the query, clear results and hide the dropdown
  if (!isSearchableTerm(query)) {
    lookupResults.value = [];
    searching.value = false;
    isOpen.value = false;
    submitted.value = false;
    return;
  }

  searching.value = true;
  try {
    const res = await store.dispatch("lookupCustomer", { terms: query });
    lookupResults.value = res.data.reduce((data, customer: LookupResult) => {
      customer.all_addresses?.forEach((address: Address) => {
        data.push({
          customer,
          address
        });
      });
      return data;
    }, []);
    isOpen.value = true;
  } catch (error) {
    console.error("Customer lookup failed:", error);
    lookupResults.value = [];
  } finally {
    searching.value = false;
    submitted.value = true;
  }
}, 500);

const handleTyping = (query: string) => {
  executeSearch(query);
  searchTerm.value = query;
  submitted.value = false;
};

const handleFormSubmit = (data: {
  query: string;
  category: string;
  filter: string;
}) => {
  executeSearch.cancel();
  executeSearch(data.query);
};

const selectCustomer = (result: {
  customer: LookupResult;
  address: Address;
}) => {
  emit("update:modelValue", result);
  isOpen.value = false;
  searchTerm.value = "";
};

const goToNewCustomer = () => {
  // On scheduler surfaces (which register this global), the customer form
  // returns the user to the dispatch board with the new appointment's
  // drawer open instead of the job page.
  const onSchedulerSurface =
    typeof window.handleSchedulerEventEdit === "function";
  isOpen.value = false;
  router.push(
    onSchedulerSurface
      ? `${userCreatePath()}?return_to=schedule`
      : userCreatePath()
  );
};
</script>

<style scoped></style>
